
import React from "react";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer
} from "recharts";

// Cores para diferentes parâmetros metabólicos
const metabolicColors = {
  glicemia: "#B5EAD7",
  insulina: "#C7CEEA",
  ldl: "#E2F0CB",
  hdl: "#FFDAC1",
  triglicerides: "#FFB7B2",
  hematocrito: "#E2F0CB"
};

// Valores de referência metabólicos
const metabolicReferences = {
  glicemia: { min: 70, max: 99, unit: "mg/dL" },
  insulina: { min: 2.6, max: 24.9, unit: "μU/mL" },
  ldl: { min: 0, max: 130, unit: "mg/dL" },
  hdl: { min: 40, max: 60, unit: "mg/dL" },
  triglicerides: { min: 0, max: 150, unit: "mg/dL" },
  hematocrito: { min: 37, max: 47, unit: "%" }
};

export function MetabolicAnalysis({ examValues }) {
  // Preparar dados para o gráfico
  const chartData = Object.entries(examValues)
    .filter(([key]) => metabolicReferences[key]) // Filtrar apenas valores metabólicos
    .map(([parameter, value]) => {
      const reference = metabolicReferences[parameter];
      return {
        name: parameter.toUpperCase(),
        value: value,
        min: reference.min,
        max: reference.max,
        unit: reference.unit,
        color: metabolicColors[parameter] || "#ff3366"
      };
    });

  // Componente de tooltip customizado
  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      const data = payload[0].payload;
      const status = data.value < data.min ? "Baixo" : 
                    data.value > data.max ? "Elevado" : "Normal";
      const statusColor = status === "Normal" ? "#4ade80" :
                         status === "Elevado" ? "#f59e0b" : "#ef4444";

      return (
        <div className="bg-[#1a1a2e]/95 p-4 rounded-lg border border-[#ff3366]/20 shadow-lg backdrop-blur-sm">
          <h4 className="text-[#ff3366] font-bold text-lg mb-2">{label}</h4>
          <div className="space-y-2">
            <div className="flex justify-between items-center">
              <span className="text-white/80">Valor:</span>
              <span className="text-white font-semibold">
                {data.value} {data.unit}
              </span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-white/80">Referência:</span>
              <span className="text-white">
                {data.min}-{data.max} {data.unit}
              </span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-white/80">Status:</span>
              <span className="px-2 py-1 rounded text-sm font-semibold" style={{ color: statusColor }}>
                {status}
              </span>
            </div>
          </div>
        </div>
      );
    }
    return null;
  };

  // Calcular HOMA-IR se glicemia e insulina estiverem disponíveis
  const homaIr = examValues.glicemia && examValues.insulina
    ? (examValues.glicemia * examValues.insulina) / 405
    : null;

  return (
    <div className="space-y-6">
      {/* Gráfico de Parâmetros Metabólicos */}
      <div className="h-[400px]">
        <ResponsiveContainer width="100%" height="100%">
          <BarChart data={chartData}>
            <CartesianGrid strokeDasharray="3 3" stroke="#ff3366" opacity={0.1} />
            <XAxis
              dataKey="name"
              stroke="#ff3366"
              tick={{ fill: "#ffffff" }}
              angle={-45}
              textAnchor="end"
              height={60}
            />
            <YAxis
              stroke="#ff3366"
              tick={{ fill: "#ffffff" }}
            />
            <Tooltip content={<CustomTooltip />} />
            <Bar
              dataKey="value"
              fill={(entry) => entry.color}
              radius={[4, 4, 0, 0]}
            />
          </BarChart>
        </ResponsiveContainer>
      </div>

      {/* Legenda e Valores */}
      <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
        {chartData.map((parameter) => (
          <div
            key={parameter.name}
            className="p-3 rounded-lg border"
            style={{
              backgroundColor: `${parameter.color}15`,
              borderColor: `${parameter.color}30`
            }}
          >
            <div className="flex items-center gap-2">
              <div
                className="w-3 h-3 rounded-full"
                style={{ backgroundColor: parameter.color }}
              />
              <span className="font-medium text-white">{parameter.name}</span>
            </div>
            <div className="mt-2 text-sm">
              <span className="text-white/90">{parameter.value} {parameter.unit}</span>
            </div>
          </div>
        ))}
      </div>

      {/* HOMA-IR */}
      {homaIr !== null && (
        <div className="p-4 rounded-lg bg-[#1a1a2e]/50 border border-[#ff3366]/20">
          <h4 className="text-lg font-semibold text-[#ff3366] mb-2">
            HOMA-IR
          </h4>
          <div className="flex items-center justify-between">
            <span className="text-white/80">Valor calculado:</span>
            <span className={`text-white font-semibold ${
              homaIr > 2.5 ? "text-red-400" : "text-green-400"
            }`}>
              {homaIr.toFixed(2)}
            </span>
          </div>
          <p className="mt-2 text-sm text-white/60">
            {homaIr > 2.5 
              ? "Sugere resistência insulínica. Recomenda-se avaliação médica."
              : "Dentro da faixa de normalidade."}
          </p>
        </div>
      )}

      {/* Interpretação */}
      <div className="p-4 rounded-lg bg-[#1a1a2e]/50 border border-[#ff3366]/20">
        <h4 className="text-lg font-semibold text-[#ff3366] mb-4">
          Interpretação
        </h4>
        <div className="space-y-2">
          {chartData.map((parameter) => {
            const status = parameter.value < parameter.min ? "baixo" :
                         parameter.value > parameter.max ? "elevado" : "normal";
            return (
              <p key={parameter.name} className="text-white/80">
                <span className="font-medium">{parameter.name}:</span> Nível {status}
                {status !== "normal" && ` - Referência: ${parameter.min}-${parameter.max} ${parameter.unit}`}
              </p>
            );
          })}
        </div>
      </div>
    </div>
  );
}
